import { useState, type ReactNode } from 'react';
import type { AgentInfo, OrgEntryInfo } from '../../api';
import type { GroupNode } from '../../devices/groupTree';
import { decodeDrag, encodeDrag, isTreeDrag, TREE_DRAG_MIME, type TreeDragItem } from '../../devices/treeDnd';

/** 들여쓰기 한 단(px) — 상수 하나로 트리 전체 간격이 바뀐다. */
export const TREE_INDENT_PX = 18;

/**
 * 그룹 트리(설계서 §14.20) — 그룹 머리(접기·개수·메뉴) + 그 아래 관리 기기·ID 항목 + 하위 그룹.
 *
 * 행의 모양·동작은 부모(기기 화면)가 `renderAgent`/`renderEntry`/`groupActions` 로 넘긴다 — 트리는 **구조와 끌어다 놓기만** 안다.
 * 끌어다 놓기는 관리자만(`draggable`): 기기·ID 항목·그룹을 그룹 머리 위에 놓으면 `onDrop(대상 그룹, 끈 것)`.
 */
export function GroupTree({
  roots,
  collapsed,
  onToggle,
  draggable,
  onDrop,
  renderAgent,
  renderEntry,
  groupActions,
  groupExtra,
}: {
  roots: GroupNode[];
  collapsed: Set<string>;
  onToggle: (groupId: string) => void;
  draggable: boolean;
  onDrop: (targetGroupId: string, item: TreeDragItem) => void;
  renderAgent: (a: AgentInfo) => ReactNode;
  renderEntry: (e: OrgEntryInfo) => ReactNode;
  /** 그룹 머리 오른쪽(메뉴 등). */
  groupActions?: (n: GroupNode) => ReactNode;
  /** 그룹 머리 바로 아래 칸(입력 폼·발급한 토큰 등). */
  groupExtra?: (n: GroupNode) => ReactNode;
}) {
  return (
    <div className="gtree" role="tree">
      {roots.map((n) => (
        <GroupBranch
          key={n.group.id}
          node={n}
          collapsed={collapsed}
          onToggle={onToggle}
          draggable={draggable}
          onDrop={onDrop}
          renderAgent={renderAgent}
          renderEntry={renderEntry}
          groupActions={groupActions}
          groupExtra={groupExtra}
        />
      ))}
    </div>
  );
}

type BranchProps = Parameters<typeof GroupTree>[0] & { node: GroupNode };

function GroupBranch(props: Omit<BranchProps, 'roots'>) {
  const { node, collapsed, onToggle, draggable, onDrop, renderAgent, renderEntry, groupActions, groupExtra } = props;
  const [over, setOver] = useState(false);
  const isOpen = !collapsed.has(node.group.id);
  const indent = (node.depth - 1) * TREE_INDENT_PX;
  const itemIndent = node.depth * TREE_INDENT_PX;

  /** 기기·ID 항목 행을 끌 수 있게 감싼다(관리자만). */
  const dragWrap = (item: TreeDragItem, child: ReactNode) => (
    <div
      key={`${item.kind}:${item.id}`}
      className="gtree-item"
      style={{ paddingLeft: itemIndent }}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData(TREE_DRAG_MIME, encodeDrag(item));
        e.dataTransfer.effectAllowed = 'move';
      }}
      role="treeitem"
    >
      {child}
    </div>
  );

  return (
    <div className="gtree-branch" role="group">
      <div
        className={`gtree-head${over ? ' drop-over' : ''}`}
        style={{ paddingLeft: indent + 6 }}
        onClick={() => onToggle(node.group.id)}
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData(TREE_DRAG_MIME, encodeDrag({ kind: 'group', id: node.group.id }));
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(e) => {
          if (!draggable || !isTreeDrag(e.dataTransfer.types)) return;
          e.preventDefault(); // 놓기 허용
          e.dataTransfer.dropEffect = 'move';
          if (!over) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          const item = decodeDrag(e.dataTransfer.getData(TREE_DRAG_MIME));
          if (!item) return;
          e.preventDefault();
          if (item.kind === 'group' && item.id === node.group.id) return; // 제자리
          onDrop(node.group.id, item);
        }}
        role="treeitem"
        aria-expanded={isOpen}
        data-group-id={node.group.id}
        title={draggable ? '기기·그룹을 여기로 끌어다 놓으면 이 그룹으로 옮깁니다' : undefined}
      >
        <span className="gtree-caret">{isOpen ? '▾' : '▸'}</span>
        <span className="gtree-icon" aria-hidden>
          📁
        </span>
        <span className="gtree-name">{node.group.name}</span>
        <span className="gtree-count" title="이 가지의 기기 수(온라인)">
          {node.total}
          {node.online > 0 ? ` · 온라인 ${node.online}` : ''}
        </span>
        <span className="gtree-head-actions">{groupActions?.(node)}</span>
      </div>
      {groupExtra && <div style={{ paddingLeft: itemIndent }}>{groupExtra(node)}</div>}
      {isOpen && (
        <>
          {node.agents.map((a) => dragWrap({ kind: 'agent', id: a.id }, renderAgent(a)))}
          {node.entries.map((e) => dragWrap({ kind: 'entry', id: e.id }, renderEntry(e)))}
          {node.children.map((c) => (
            <GroupBranch key={c.group.id} {...props} node={c} />
          ))}
          {node.total === 0 && node.children.length === 0 && (
            <div className="hint gtree-empty" style={{ paddingLeft: itemIndent + 6 }}>
              비어 있음
            </div>
          )}
        </>
      )}
    </div>
  );
}
